import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EditorTab } from '@models/editorState/editorTab.model';
import { EditorStateService } from '@services/editorState/editor-state.service';
import { TinymceConfigService } from '@services/tinymce/tinymce-config/tinymce-config.service';
import { EditorComponent } from '@tinymce/tinymce-angular';
import { NgxEditorModule } from 'ngx-editor';
import { NgxSpinnerModule } from 'ngx-spinner';
import { Subject, takeUntil, debounceTime } from 'rxjs';

@Component({
  selector: 'app-tinymce-editor',
  standalone: true,
  imports: [CommonModule,MatIconModule,MatButtonModule,NgxSpinnerModule,MatFormFieldModule,ReactiveFormsModule,MatProgressSpinnerModule,EditorComponent,MatTooltipModule],
  templateUrl: './tinymce-editor.component.html',
  styleUrl: './tinymce-editor.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TinymceEditorComponent {

  @Input() tabId!: string;
  @ViewChild('editor', { static: true }) editorElement!: ElementRef;

  currentTab?: EditorTab;
  editorConfig: any;
  isEditorInitialized = false;

  private contentChangeSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private editorStateService: EditorStateService,
    private tinymceConfigService: TinymceConfigService
  ) {}

  ngOnInit(): void {
    this.setupEditor();
    this.subscribeToTabChanges();
    this.setupContentChangeHandler();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupEditor(): void {
    this.editorConfig = {
      ...this.tinymceConfigService.getConfig(),
      setup: (editor: any) => {
        // Configuración personalizada del editor
        this.tinymceConfigService.getConfig().setup(editor);

        // Eventos personalizados
        editor.on('init', () => {
          this.isEditorInitialized = true;
          this.loadTabContent();
        });

        editor.on('input keyup paste', () => {
          const content = editor.getContent();
          this.contentChangeSubject.next(content);
        });

        // Shortcuts de teclado
        editor.addShortcut('ctrl+s', 'Guardar', () => {
          this.saveCurrentTab();
        });

        editor.addShortcut('ctrl+shift+s', 'Guardar como nueva versión', () => {
          this.saveAsNewVersion();
        });
      }
    };
  }

  private subscribeToTabChanges(): void {
    this.editorStateService.getEditorState()
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        const tab = state.tabs.find(t => t.id === this.tabId);
        if (tab && tab !== this.currentTab) {
          this.currentTab = tab;
          if (this.isEditorInitialized) {
            this.loadTabContent();
          }
        }
      });
  }

  private setupContentChangeHandler(): void {
    this.contentChangeSubject
      .pipe(
        debounceTime(500),
        takeUntil(this.destroy$)
      )
      .subscribe(content => {
        if (this.currentTab) {
          this.editorStateService.updateTabContent(this.tabId, content);
        }
      });
  }

  private loadTabContent(): void {
    if (!this.currentTab || !this.isEditorInitialized) return;

    // Obtener la instancia del editor
    const editor = (window as any).tinymce?.get(this.editorElement.nativeElement.id);
    if (editor) {
      editor.setContent(this.currentTab.contenido || '');
    }
  }

  onEditorInit(event: any): void {
    this.isEditorInitialized = true;
    this.loadTabContent();
  }

  onContentChange(event: any): void {
    if (event.editor) {
      const content = event.editor.getContent();
      this.contentChangeSubject.next(content);
    }
  }

  private saveCurrentTab(): void {
    if (!this.currentTab) return;

    this.editorStateService.saveActiveTab().subscribe({
      next: (savedEntity) => {
        console.log('Tab guardado exitosamente:', savedEntity);
        // Aquí podrías mostrar un toast de confirmación
      },
      error: (error) => {
        console.error('Error guardando tab:', error);
        // Aquí podrías mostrar un toast de error
      }
    });
  }

  private saveAsNewVersion(): void {
    if (!this.currentTab || this.currentTab.tipo !== 'escrito' || !this.currentTab.entityId) {
      return;
    }

    // Implementar guardado como nueva versión
    console.log('Guardar como nueva versión:', this.currentTab);
  }

  // Métodos públicos para acciones del editor
  insertVariable(variableCode: string): void {
    const editor = (window as any).tinymce?.get(this.editorElement.nativeElement.id);
    if (editor) {
      editor.insertContent(`{{${variableCode}}}`);
    }
  }

  processVariables(): void {
    const editor = (window as any).tinymce?.get(this.editorElement.nativeElement.id);
    if (editor) {
      const content = editor.getContent();

      // Por ahora solo mostrar las variables encontradas
      const variableRegex = /\{\{([^}]+)\}\}/g;
      const variables: string[] = [];
      let match;

      while ((match = variableRegex.exec(content)) !== null) {
        variables.push(match[1]);
      }

      if (variables.length > 0) {
        console.log('Variables encontradas para procesar:', variables);
        // Aquí se llamaría a la API de procesamiento cuando esté disponible
      }
    }
  }

  focusEditor(): void {
    const editor = (window as any).tinymce?.get(this.editorElement.nativeElement.id);
    if (editor) {
      editor.focus();
    }
  }

  getEditorContent(): string {
    const editor = (window as any).tinymce?.get(this.editorElement.nativeElement.id);
    return editor ? editor.getContent() : '';
  }

  setEditorContent(content: string): void {
    const editor = (window as any).tinymce?.get(this.editorElement.nativeElement.id);
    if (editor) {
      editor.setContent(content);
    }
  }
}

